/**
 * Service layer — Parcels domain (CT-020 Parcel Research)
 *
 * Owns: Parcels (tblBcOe6dw3wSlave), and the linked reference records the
 * parcel research tool reads/writes:
 *   Zones & Special Districts (tblZa2A6sU6dKtuKR)
 *   Jurisdictions            (tblYvArOwofZnsepR)
 *   Organizations / agencies (tblsPUYYYOTNkiXUt)
 *   Historical Permits       (tblIKtoPPgonVrGpf)
 *
 * Parcels stay in Airtable; all Laguna-Beach metric/coverage/setback values are
 * Airtable formula fields — this layer reads the computed results and never
 * recomputes them. See docs/PARCEL_RESEARCH.md.
 *
 * "Primary parcel" is, for now, the parcel with the lowest Project Parcel ID
 * (no dedicated flag field yet — see the doc's refinement backlog).
 */

import { apiGet, apiGetOne, apiPost, apiPatch, apiDelete } from './_api.js'
import { searchOrganizations, getOrgNamesByIds, createOrganization, recordContactOrigin } from './contacts.js'

const TBL_PARCELS = 'tblBcOe6dw3wSlave'
const TBL_ZONES   = 'tblZa2A6sU6dKtuKR'
const TBL_JURIS   = 'tblYvArOwofZnsepR'
const TBL_ORGS    = 'tblsPUYYYOTNkiXUt'
const TBL_HIST    = 'tblIKtoPPgonVrGpf'

const getFirst = v => (Array.isArray(v) ? v[0] : v)

/* ── Reads ─────────────────────────────────────────────────────────────── */

/** Fetch all Parcels for a project (list display: APN + zone pill + zoom). */
export function getParcels(projectId) {
  const formula = 'FIND("' + projectId + '",ARRAYJOIN({Project Record UID (from Project)},","))'
  return apiGet(
    TBL_PARCELS,
    'filterByFormula=' + encodeURIComponent(formula)
    + '&sort%5B0%5D%5Bfield%5D=Project+Parcel+ID&sort%5B0%5D%5Bdirection%5D=asc'
    + '&maxRecords=20'
  )
}

/** Fetch a single Parcel record (all fields — lookups must round-trip). */
export function getParcel(parcelId) {
  return apiGetOne(TBL_PARCELS, parcelId)
}

/**
 * Fetch a parcel plus its linked Zone and Jurisdiction records.
 * The Jurisdiction record already carries the denormalized Planning/Building/
 * Fire/Health department contact + submission + turnaround + expediting fields,
 * so no per-Organization fetch is needed for the four standard departments.
 * @returns {Promise<{ parcel, zone, jurisdiction }>}
 */
export async function getParcelDetail(parcelId) {
  const parcel = await getParcel(parcelId)
  if (!parcel) return { parcel: null, zone: null, jurisdiction: null }

  const zoneId = getFirst(parcel.fields['Zone'])
  const zone = zoneId ? await apiGetOne(TBL_ZONES, zoneId) : null

  // Jurisdiction: prefer the zone's link, fall back to the parcel's direct link.
  let jurisId = zone ? getFirst(zone.fields['Jurisdiction']) : null
  if (!jurisId) jurisId = getFirst(parcel.fields['Jurisdictions'])
  const jurisdiction = jurisId ? await apiGetOne(TBL_JURIS, jurisId) : null

  // Special Overlay is a second link into the same Zones table; Owner links to an Organization.
  // Both are resolved to names here so every screen shows the name, not a bare "Yes".
  const overlayIds = [].concat(parcel.fields['Special Overlay'] || []).filter(Boolean)
  const overlays = (await Promise.all(overlayIds.map(id => apiGetOne(TBL_ZONES, id).catch(() => null)))).filter(Boolean)
  const ownerIds = [].concat(parcel.fields['Owner'] || []).filter(Boolean)
  const ownerNames = ownerIds.length ? await getOrgNamesByIds(ownerIds) : {}

  return { parcel, zone, jurisdiction, overlays, ownerNames }
}

/** A zone record's display name. */
export function zoneDisplayName(zoneFields) {
  const z = zoneFields || {}
  return z['Name of Zone'] || z['Name'] || ''
}

/** Existing Approvals — Historical Permit child rows for a parcel, newest first. */
export function getHistoricalPermits(parcelId) {
  const formula = 'FIND("' + parcelId + '",ARRAYJOIN({Record ID (from Parcel)},","))'
  return apiGet(
    TBL_HIST,
    'filterByFormula=' + encodeURIComponent(formula)
    + '&sort%5B0%5D%5Bfield%5D=Permit+Date&sort%5B0%5D%5Bdirection%5D=desc'
    + '&maxRecords=200'
  )
}

/**
 * All agency Organizations under a jurisdiction (the full Departments list,
 * incl. checklist files). Uses the jurisdiction's Departments link — a
 * multipleRecordLinks field returns real Organization record IDs, so we fetch
 * each by ID (rule-compliant; ARRAYJOIN on a link returns names, not IDs).
 * @returns {Promise<object[]>} Organization records with Type 'Agency'
 */
export async function getJurisdictionAgencies(jurisdictionId) {
  const juris = await apiGetOne(TBL_JURIS, jurisdictionId)
  const deptIds = (juris && juris.fields['Departments']) || []
  if (!deptIds.length) return []
  const orgs = await Promise.all(deptIds.map(id => apiGetOne(TBL_ORGS, id)))
  // Organizations.Type is a multipleSelects field (confirmed live: e.g. Type:['Agency']) — a
  // bare `=== 'Agency'` string compare here always fails against an array, which silently
  // returned zero agencies for every jurisdiction. Fixed 2026-09 while building the Permitting
  // workspace's Add Department action on top of this same function.
  // HOAs are stored with Type ['HOA'] (not 'Agency') but are a real review body on a
  // jurisdiction's list, so both types count. Before 2026-09-30 an HOA linked here was hidden.
  return orgs.filter(o => o && [].concat(o.fields['Type'] || []).some(t => DEPARTMENT_ORG_TYPES.includes(t)))
}

/** Organization Types that count as a jurisdiction department / review body. */
const DEPARTMENT_ORG_TYPES = ['Agency', 'HOA']

/**
 * Search existing Organizations to link onto a jurisdiction's Departments list (the Permitting
 * workspace's "Add Department" picker). Every type is returned, agencies and HOAs first, so an
 * org whose Type was never set can still be found; the picker shows the type beside each name.
 */
export async function searchDepartmentOrganizations(term) {
  const r = await searchOrganizations(term || '')
  const recs = (r && r.contacts) || []
  const isDept = o => [].concat(o.fields['Type'] || []).some(t => DEPARTMENT_ORG_TYPES.includes(t))
  return recs
    .map(o => ({
      id: o.id,
      name: o.fields['Organization Name'] || '(unnamed)',
      types: [].concat(o.fields['Type'] || []),
      city: o.fields['Client City'] || '',
      isDept: isDept(o),
    }))
    .sort((a, b) => (b.isDept - a.isDept) || a.name.localeCompare(b.name))
}

/**
 * Link an existing Organization onto a jurisdiction's Departments list. Writes the Jurisdiction's
 * own `Departments` link (read-modify-write so existing links are kept). SHARED: the list is the
 * jurisdiction's, so it shows on every project in that jurisdiction.
 */
export async function linkDepartment(jurisdictionId, orgId) {
  const juris = await apiGetOne(TBL_JURIS, jurisdictionId)
  const ids = [].concat((juris && juris.fields['Departments']) || [])
  if (ids.includes(orgId)) return
  await apiPatch(TBL_JURIS, jurisdictionId, { 'Departments': [...ids, orgId] })
}

/**
 * Remove an Organization from a jurisdiction's Departments list. Only the link goes; the
 * Organization record itself (and its links to other jurisdictions) is untouched.
 */
export async function unlinkDepartment(jurisdictionId, orgId) {
  const juris = await apiGetOne(TBL_JURIS, jurisdictionId)
  const ids = [].concat((juris && juris.fields['Departments']) || [])
  if (!ids.includes(orgId)) return
  await apiPatch(TBL_JURIS, jurisdictionId, { 'Departments': ids.filter(id => id !== orgId) })
}

/**
 * Search Zones & Special Districts for the zone picker.
 * Matches the search term against the zone name; returns light objects.
 */
export function getZones(searchTerm) {
  let qs = 'sort%5B0%5D%5Bfield%5D=Name&sort%5B0%5D%5Bdirection%5D=asc&maxRecords=50'
  if (searchTerm) {
    const t = searchTerm.replace(/"/g, '')
    const f = 'OR(FIND(LOWER("' + t + '"),LOWER({Name})),FIND(LOWER("' + t + '"),LOWER({Name of Zone})))'
    qs = 'filterByFormula=' + encodeURIComponent(f) + '&' + qs
  }
  return apiGet(TBL_ZONES, qs).then(async recs => {
    const zones = recs.map(r => ({
      id: r.id,
      name: r.fields['Name of Zone'] || r.fields['Name'] || r.id,
      jurisdiction: getFirst(r.fields['Jurisdiction']) || null,
      type: r.fields['Type'] || 'Zone',
    }))
    // Zone names repeat across cities ("R1" exists in Costa Mesa AND Pismo Beach), so the picker
    // shows each zone's jurisdiction beside it. Names come from the Jurisdiction records
    // themselves, not the Zone's formula `Name`, so this survives the Supabase migration.
    const names = await getJurisdictionNames(zones.map(z => z.jurisdiction))
    return zones.map(z => ({ ...z, jurisdictionName: (z.jurisdiction && names[z.jurisdiction]) || '' }))
  })
}

// Jurisdiction id -> Name, cached for the session (a small, rarely-edited reference table).
const jurisNameCache = {}
async function getJurisdictionNames(ids) {
  const want = [...new Set((ids || []).filter(Boolean))].filter(id => !(id in jurisNameCache))
  if (want.length) {
    const f = 'OR(' + want.map(id => 'RECORD_ID()="' + id + '"').join(',') + ')'
    try {
      const recs = await apiGet(TBL_JURIS, 'filterByFormula=' + encodeURIComponent(f) + '&fields%5B%5D=Name&maxRecords=100')
      for (const r of recs || []) jurisNameCache[r.id] = r.fields['Name'] || ''
    } catch { /* non-fatal — the picker just shows zone names alone */ }
  }
  const out = {}
  for (const id of ids || []) if (id && jurisNameCache[id] != null) out[id] = jurisNameCache[id]
  return out
}

/* ── Permitting workspace: Jurisdictions & Zones library ─────────────────────────────────
 * Added 2026-09 (Permitting workspace, CT-020 follow-on) — add/edit Jurisdictions and Zones
 * from the app instead of Airtable directly. See docs/PERMITTING_WORKSPACE.md.
 */

/** Search/list Jurisdictions for the Permitting workspace's left-panel list. */
export function getJurisdictions(searchTerm) {
  let qs = 'sort%5B0%5D%5Bfield%5D=Name&sort%5B0%5D%5Bdirection%5D=asc&maxRecords=100'
  if (searchTerm) {
    const t = searchTerm.replace(/"/g, '')
    qs = 'filterByFormula=' + encodeURIComponent('FIND(LOWER("' + t + '"),LOWER({Name}))') + '&' + qs
  }
  return apiGet(TBL_JURIS, qs)
}

/** Fetch a single Jurisdiction record (all fields). */
export function getJurisdiction(jurisdictionId) {
  return apiGetOne(TBL_JURIS, jurisdictionId)
}

/** Create a new Jurisdiction (Name/State/Type/address/links required by the create form). */
export function createJurisdiction(fields) {
  return apiPost(TBL_JURIS, fields, { typecast: true })
}

/**
 * PATCH a Jurisdiction record. SHARED across every zone/parcel that references it — callers
 * MUST confirm with the user before invoking (mirrors updateZoneField's shared-record warning).
 */
export function updateJurisdictionField(jurisdictionId, fields) {
  return apiPatch(TBL_JURIS, jurisdictionId, fields, { typecast: true })
}

/** All Zones & Special Districts under one Jurisdiction, for the Permitting workspace's zone list. */
export function getZonesForJurisdiction(jurisdictionId) {
  const formula = 'FIND("' + jurisdictionId + '",ARRAYJOIN({Record ID (from Jurisdiction)},","))'
  return apiGet(TBL_ZONES, 'filterByFormula=' + encodeURIComponent(formula)
    + '&sort%5B0%5D%5Bfield%5D=Name+of+Zone&sort%5B0%5D%5Bdirection%5D=asc&maxRecords=200')
}

/** Fetch a single Zone record (all fields). */
export function getZone(zoneId) {
  return apiGetOne(TBL_ZONES, zoneId)
}

/** Create a new Zone under a Jurisdiction. */
export function createZone(jurisdictionId, fields) {
  return apiPost(TBL_ZONES, { 'Jurisdiction': [jurisdictionId], ...fields }, { typecast: true })
}

/* ── Writes (Phase 2) ──────────────────────────────────────────────────── */

/** PATCH parcel fields. typecast on so select values match leniently. */
export function updateParcelField(parcelId, fields) {
  return apiPatch(TBL_PARCELS, parcelId, fields, { typecast: true })
}

/**
 * PATCH a Zone record. SHARED across every project in this zone — callers MUST
 * confirm with the user before invoking (see ParcelResearchTool shared-record
 * warning).
 */
export function updateZoneField(zoneId, fields) {
  return apiPatch(TBL_ZONES, zoneId, fields, { typecast: true })
}

/**
 * PATCH an agency Organization. SHARED across every jurisdiction/project that
 * references it — confirm before calling.
 */
export function updateAgency(orgId, fields) {
  return apiPatch(TBL_ORGS, orgId, fields, { typecast: true })
}

/** Create a new agency Organization linked to a jurisdiction. `Type` is a multipleSelects
 * field on Organizations — must POST an array, not a bare string (see getJurisdictionAgencies). */
export async function addAgency(jurisdictionId, fields) {
  // An HOA is Type ['HOA'] everywhere else in the base (the Contacts HOA filter, the report's
  // separate HOA section), so an HOA/Design Review department is created that way too.
  const isHoa = fields && fields['Agency Type'] === 'HOA/Design Review'
  const rec = await apiPost(TBL_ORGS, {
    'Type': [isHoa ? 'HOA' : 'Agency'],
    'Jurisdictions': [jurisdictionId],
    ...fields,
  }, { typecast: true })
  if (rec && rec.id) recordContactOrigin('org', rec.id, 'parcel_research', isHoa ? 'HOA' : 'agency')
  return rec
}

/** Create a Historical Permit (Existing Approval) row on a parcel. */
export function addHistoricalPermit(parcelId, fields) {
  return apiPost(TBL_HIST, { 'Parcel': [parcelId], ...fields })
}

/** Patch a Historical Permit row. */
export function updateHistoricalPermit(permitId, fields) {
  return apiPatch(TBL_HIST, permitId, fields)
}

/** Delete a Historical Permit row. */
export function deleteHistoricalPermit(permitId) {
  return apiDelete(TBL_HIST, permitId)
}

/** Create a new, blank Parcel linked to a project (Add Parcel — not a duplicate). */
export function addParcel(projectId, fields = {}) {
  return apiPost(TBL_PARCELS, { 'Project': [projectId], ...fields }, { typecast: true })
}

/**
 * Duplicate a parcel's research into another project under a new parcel number.
 * Copies the editable research inputs + zone/overlay/jurisdiction/association
 * links; skips formula fields (Airtable recomputes them) and project-scoped
 * identifiers. Returns the created parcel record.
 */
export async function duplicateParcel(parcelId, targetProjectId, parcelNumber) {
  const src = await getParcel(parcelId)
  if (!src) throw new Error('Source parcel not found')
  const f = src.fields

  const COPY_LINKS = ['Zone', 'Special Overlay', 'Jurisdictions', 'Association', 'Owner']
  const COPY_SCALARS = [
    'APN', 'Legal Description', 'Lot Size SF',
    'Coastal Zone', 'Oceanfront Lot', 'FHSZ', 'FHSZ Responsibility',
    'Flood Zone or Water Course', 'Landslide Zone', 'Liquefaction', 'Fault Lines', 'WUI',
    'Allowed Lot Use', 'Existing Lot Use', 'Project Specific Permitting Notes',
    'Required Front Yard Setback', 'Required Rear Yard Setback',
    'Required Side Yard Setback 1', 'Required Side Yard Setback 2',
    'Required Covered Parking Spaces', 'Required Uncovered Parking Spaces',
    'Required Open Space Percent',
    'Allowable Height from Grade Manual',
    'Allowable Height from Rear Lot Line Manual', 'Allowable Height from Front Lot Line Manual',
    'Allowable Lot Coverage Manual Percent',
    'Average Lot Depth', 'Average Lot Width',
    'Lot High Corner 1 Elevation', 'Lot High Corner 2 Elevation',
    'Lot Low Corner 1 Elevation', 'Lot Low Corner 2 Elevation',
    'Height at Midpoint of Front Curb or CL of Travelled Way',
    'Height of Natural Grade 40 Feet in from Midpoint of Front Lot Line',
    'LB Site Slope Configuration', 'Front Curb',
    'Existing Height from Grade FF', 'Existing Height from Slope Datum',
    'Existing Height Approximate', 'Existing Setbacks Approximate',
    'Existing Lot Coverage SF', 'Existing Open Space SF', 'Existing Landscaped Open Space SF',
    'Existing Front Yard Setback', 'Existing Rear Yard Setback',
    'Existing Side Yard Setback 1', 'Existing Side Yard Setback 2',
    'Existing Covered Parking Spaces', 'Existing Uncovered Parking Spaces',
  ]

  const fields = { 'Project': [targetProjectId] }
  if (parcelNumber != null && parcelNumber !== '') fields['Project Parcel ID'] = Number(parcelNumber)
  for (const k of COPY_LINKS) if (Array.isArray(f[k]) && f[k].length) fields[k] = f[k]
  for (const k of COPY_SCALARS) if (f[k] != null && f[k] !== '') fields[k] = f[k]

  const created = await apiPost(TBL_PARCELS, fields, { typecast: true })
  // Carry the HOA answer over too (it lives in Supabase, not on the Airtable record).
  try {
    const ans = await getParcelHoaAnswer(parcelId)
    if (ans && created && created.id) await setParcelHoaAnswer(created.id, ans, 'Copied with parcel')
  } catch { /* non-fatal — the copy just starts as Not researched */ }
  // And the setback map (Supabase, database/307), if this lot has one.
  try {
    if (created && created.id) await setbackMapPost({ action: 'copy', fromParcelId: parcelId, toParcelId: created.id })
  } catch { /* non-fatal — the copy just follows the zone standard */ }
  // And the exhibits (Supabase, database/349) — the rows are copied, the files are shared.
  try {
    if (created && created.id) await exhibitsPost({ action: 'copy', fromParcelId: parcelId, toParcelId: created.id })
  } catch { /* non-fatal — the copy just starts with no exhibits */ }
  return created
}

/* ── HOA status (2026-09-28) ─────────────────────────────────────────────────────────────
 * Replaces the Airtable `Association Overlay` checkbox, which could not tell "researched, no
 * HOA" from "never looked up". The explicit answer lives in Supabase `parcel_hoa_status`
 * (database/278, api/parcel-hoa.js); the linked Airtable `Association` stays the source of
 * truth for WHICH HOA. `Association Overlay` is frozen history — nothing reads or writes it.
 */

/* ── Zone code references with no Airtable field (Supabase, database/280) ───────────────── */

async function zoneRefsPost(payload) {
  const headers = { 'Content-Type': 'application/json' }
  if (typeof window !== 'undefined' && typeof window.__clerkGetToken === 'function') {
    try { const t = await window.__clerkGetToken(); if (t) headers['Authorization'] = 'Bearer ' + t } catch { /* unauthed */ }
  }
  const res = await fetch('/api/zone-code-refs', { method: 'POST', headers, body: JSON.stringify(payload) })
  const data = await res.json().catch(() => ({ error: 'Bad response from /api/zone-code-refs' }))
  if (!res.ok || data.error) throw new Error(data.error || ('Request failed (' + res.status + ')'))
  return data
}

/** A zone's parking code reference ('' when none). */
export async function getZoneParkingCodeRef(zoneId) {
  return (await zoneRefsPost({ action: 'get', zoneId })).parkingCodeReference || ''
}

/** Save a zone's parking code reference. Blank clears it. SHARED across every parcel in the zone. */
export async function setZoneParkingCodeRef(zoneId, value, byName) {
  return (await zoneRefsPost({ action: 'set', zoneId, parkingCodeReference: value, byName })).parkingCodeReference || ''
}

async function hoaPost(payload) {
  const headers = { 'Content-Type': 'application/json' }
  if (typeof window !== 'undefined' && typeof window.__clerkGetToken === 'function') {
    try { const t = await window.__clerkGetToken(); if (t) headers['Authorization'] = 'Bearer ' + t } catch { /* unauthed */ }
  }
  const res = await fetch('/api/parcel-hoa', { method: 'POST', headers, body: JSON.stringify(payload) })
  const data = await res.json().catch(() => ({ error: 'Bad response from /api/parcel-hoa' }))
  if (!res.ok || data.error) throw new Error(data.error || ('Request failed (' + res.status + ')'))
  return data
}

/** The stored answer for a parcel: 'none' | 'has' | null (no answer recorded). */
export async function getParcelHoaAnswer(parcelId) {
  return (await hoaPost({ action: 'get', parcelId })).status
}

/** Record the answer. null clears it (back to "Not researched"). */
export async function setParcelHoaAnswer(parcelId, status, byName) {
  return (await hoaPost({ action: 'set', parcelId, status, byName })).status
}

/**
 * The status the app shows: 'has' | 'none' | 'unknown'.
 * A linked Association always means Has HOA; otherwise the stored answer; otherwise unknown.
 */
export function hoaStatusFor(parcelFields, storedAnswer) {
  const linked = [].concat((parcelFields && parcelFields['Association']) || []).filter(Boolean)
  if (linked.length) return 'has'
  if (storedAnswer === 'none' || storedAnswer === 'has') return storedAnswer
  return 'unknown'
}

/** Search Organizations for the HOA picker → [{ id, name }]. */
export async function searchHoaOrganizations(q) {
  const r = await searchOrganizations(q)
  return ((r && r.contacts) || []).map(o => ({ id: o.id, name: (o.fields && o.fields['Organization Name']) || o.id }))
}

/** Search Jurisdictions for the parcel's city picker → [{ id, name, sub }]. */
export async function searchJurisdictionOptions(q) {
  const recs = await getJurisdictions(q)
  return (recs || []).map(r => ({
    id: r.id,
    name: r.fields['Name'] || r.id,
    sub: [r.fields['Type'], r.fields['State']].filter(Boolean).join(' · '),
  }))
}

/** Create a new HOA Organization (Type HOA) from the parcel's HOA picker → { id, name }.
 *  Contact details are filled in later from Contacts; the name is enough to link and report it. */
export async function createHoaOrganization(name) {
  const rec = await createOrganization({ 'Organization Name': name, 'Type': ['HOA'] }, 'parcel_research')
  return { id: rec.id, name: (rec.fields && rec.fields['Organization Name']) || name }
}

/** Organization ids → { id: name } (for showing the linked HOA's name). */
export function getHoaNames(ids) {
  return getOrgNamesByIds(ids)
}

/* ── Setback map (2026-10-16, Supabase database/307, api/parcel-setback-map.js) ───────────
 * Some cities keep setback maps that set a lot's setbacks differently from the zone standard
 * (Santiago: the map treats the rear as the front). A record here means "this lot's setbacks come
 * from the map"; the required numbers stay on the Airtable parcel. The Pre-Design report prints
 * the zone standard and this lot's deviation side by side.
 */

async function setbackMapPost(payload) {
  const headers = { 'Content-Type': 'application/json' }
  if (typeof window !== 'undefined' && typeof window.__clerkGetToken === 'function') {
    try { const t = await window.__clerkGetToken(); if (t) headers['Authorization'] = 'Bearer ' + t } catch { /* unauthed */ }
  }
  const res = await fetch('/api/parcel-setback-map', { method: 'POST', headers, body: JSON.stringify(payload) })
  const data = await res.json().catch(() => ({ error: 'Bad response from /api/parcel-setback-map' }))
  if (!res.ok || data.error) throw new Error(data.error || ('Request failed (' + res.status + ')'))
  return data
}

/** The parcel's setback map record, or null when the lot follows the zone standard.
 *  Shape: { mapCitation, frontLine, rearLine, side1Line, side2Line, explanation, clipPath, clipUrl, clipCaption } */
export async function getParcelSetbackMap(parcelId) {
  return fromSetbackRow((await setbackMapPost({ action: 'get', parcelId })).map)
}

/** Save some fields (partial). Creates the record the first time — which turns the map on. */
export async function saveParcelSetbackMap(parcelId, fields, byName) {
  return fromSetbackRow((await setbackMapPost({ action: 'set', parcelId, fields: toSetbackRow(fields), byName })).map)
}

/** Turn the map off: the lot follows the zone standard again (removes the clip too). */
export async function clearParcelSetbackMap(parcelId) {
  await setbackMapPost({ action: 'clear', parcelId })
  return null
}

/** Upload a clip of the map (an image Blob/File). Converted to PNG and capped at 2000 px so the
 *  report can embed it. Returns the saved record. */
export async function uploadParcelSetbackClip(parcelId, file, byName) {
  const png = await toPngBlob(file, 2000)
  const { signedUrl, path } = await setbackMapPost({ action: 'clipUploadUrl', parcelId })
  const up = await fetch(signedUrl, { method: 'PUT', headers: { 'Content-Type': 'image/png', 'x-upsert': 'true' }, body: png })
  if (!up.ok) throw new Error('Storage upload failed (' + up.status + ')')
  return saveParcelSetbackMap(parcelId, { clipPath: path }, byName)
}

const SETBACK_KEYS = {
  mapCitation: 'map_citation', frontLine: 'front_line', rearLine: 'rear_line', side1Line: 'side1_line',
  side2Line: 'side2_line', explanation: 'explanation', clipPath: 'clip_path', clipCaption: 'clip_caption',
}
function toSetbackRow(fields) {
  const out = {}
  for (const [k, col] of Object.entries(SETBACK_KEYS)) if (k in (fields || {})) out[col] = fields[k]
  return out
}
function fromSetbackRow(row) {
  if (!row) return null
  const out = { clipUrl: row.clip_url || null, updatedBy: row.updated_by_name || null }
  for (const [k, col] of Object.entries(SETBACK_KEYS)) out[k] = row[col] || ''
  return out
}

function toPngBlob(file, maxDim) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      const k = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight))
      const c = document.createElement('canvas')
      c.width = Math.max(1, Math.round(img.naturalWidth * k)); c.height = Math.max(1, Math.round(img.naturalHeight * k))
      const g = c.getContext('2d')
      g.fillStyle = '#fff'; g.fillRect(0, 0, c.width, c.height)
      g.drawImage(img, 0, 0, c.width, c.height)
      URL.revokeObjectURL(url)
      c.toBlob(b => b ? resolve(b) : reject(new Error('Could not read the image')), 'image/png')
    }
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('That file is not an image')) }
    img.src = url
  })
}

/* ── Exhibits (2026-10-26, AT-PZ6S6 — Supabase database/349, api/parcel-exhibits.js) ──────
 * An exhibit is one image or one PDF page that verifies a research finding (the FHSZ map, the
 * zoning map), tied to that finding by `topic`. The Pre-Design report marks the finding
 * "see Exhibit N" and prints the exhibits at the end.
 */

export const EXHIBIT_TOPICS = [
  { key: 'zoning', label: 'Zoning map', finding: 'Zone' },
  { key: 'overlay', label: 'Overlay or special district', finding: 'Special Overlay' },
  { key: 'coastal', label: 'Coastal zone map', finding: 'Coastal Zone' },
  { key: 'fhsz', label: 'Fire hazard severity zone map', finding: 'FHSZ' },
  { key: 'flood', label: 'Flood zone map', finding: 'Flood Zone or Water Course' },
  { key: 'landslide', label: 'Landslide zone map', finding: 'Landslide Zone' },
  { key: 'liquefaction', label: 'Liquefaction zone map', finding: 'Liquefaction' },
  { key: 'fault', label: 'Fault zone map', finding: 'Fault Lines' },
  { key: 'other', label: 'Other', finding: null },
]
export const exhibitTopicLabel = key => (EXHIBIT_TOPICS.find(t => t.key === key) || EXHIBIT_TOPICS[EXHIBIT_TOPICS.length - 1]).label

const EXHIBIT_PDF_MAX_BYTES = 25 * 1024 * 1024

async function exhibitsPost(payload) {
  const headers = { 'Content-Type': 'application/json' }
  if (typeof window !== 'undefined' && typeof window.__clerkGetToken === 'function') {
    try { const t = await window.__clerkGetToken(); if (t) headers['Authorization'] = 'Bearer ' + t } catch { /* unauthed */ }
  }
  const res = await fetch('/api/parcel-exhibits', { method: 'POST', headers, body: JSON.stringify(payload) })
  const data = await res.json().catch(() => ({ error: 'Bad response from /api/parcel-exhibits' }))
  if (!res.ok || data.error) throw new Error(data.error || ('Request failed (' + res.status + ')'))
  return data
}

function fromExhibitRow(r) {
  return {
    id: r.id, topic: r.topic, kind: r.file_kind, url: r.url, fileName: r.file_name || '',
    caption: r.caption || '', source: r.source || '', sortOrder: r.sort_order, addedBy: r.created_by_name || '',
  }
}

/** Every exhibit on a parcel, in report order. */
export async function getParcelExhibits(parcelId) {
  return ((await exhibitsPost({ action: 'list', parcelId })).exhibits || []).map(fromExhibitRow)
}

/** Upload one exhibit. `file` is an image (converted to PNG, capped at 2400 px so the report can
 *  embed it) or a ONE-PAGE PDF (the caller runs the page picker first). Returns the new exhibit. */
export async function addParcelExhibit(parcelId, file, { topic, caption, source, byName } = {}) {
  const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '')
  let blob = file
  if (isPdf) {
    if (file.size > EXHIBIT_PDF_MAX_BYTES) throw new Error('That PDF is over 25 MB. Save just the page you need and try again.')
  } else {
    blob = await toPngBlob(file, 2400)
  }
  const kind = isPdf ? 'pdf' : 'image'
  const { signedUrl, path } = await exhibitsPost({ action: 'uploadUrl', parcelId, kind })
  const up = await fetch(signedUrl, { method: 'PUT', headers: { 'Content-Type': isPdf ? 'application/pdf' : 'image/png', 'x-upsert': 'true' }, body: blob })
  if (!up.ok) throw new Error('Storage upload failed (' + up.status + ')')
  const { exhibit } = await exhibitsPost({ action: 'add', parcelId, path, kind, topic, caption, source, byName, fileName: file.name || (isPdf ? 'exhibit.pdf' : 'Pasted image') })
  return fromExhibitRow(exhibit)
}

/** Edit an exhibit's topic, caption, source or order. */
export async function updateParcelExhibit(id, fields) {
  const map = { topic: 'topic', caption: 'caption', source: 'source', sortOrder: 'sort_order' }
  const out = {}
  for (const [k, col] of Object.entries(map)) if (k in (fields || {})) out[col] = fields[k]
  return fromExhibitRow((await exhibitsPost({ action: 'update', id, fields: out })).exhibit)
}

export async function removeParcelExhibit(id) {
  await exhibitsPost({ action: 'remove', id })
}
